import { useContext } from 'react';

import { mapSiteAccountModelToDto } from '@hotjar/account';
import type { Plan } from '@hotjar/webapp/domains/revenue/plan/plan';
import type { FeatureGroup } from '@hotjar/webapp/domains/revenue/plan/types';
import { FeatureGroupEnum } from '@hotjar/webapp/domains/revenue/plan/types';
import { usePricingTableByAccount } from '@hotjar/webapp/hooks/usePricingTable';

import type {
  AnalyticsProduct,
  FeedbackProduct,
} from '@hotjar/webapp/domains/common/product/types';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import { getProductSkusByPlanId, isProduct } from '@hotjar/webapp/domains/common/product/utils';
import { useProductComparators } from '@hotjar/webapp/domains/common/product/hooks/useProductComparators';
import { PlanComparisonColumnsContext } from '@hotjar/webapp/features/Billing/PlanComparisonColumns/utils';

export interface UsePlanAvailabilityProps {
  featureGroup: FeatureGroup;
  selectedPlan: Plan | AnalyticsProduct | FeedbackProduct;
  recommendedPlan: Plan | AnalyticsProduct | FeedbackProduct | null;
  sku?: ProductType;
}

export function isPlan(
  maybeIsPlan: Plan | AnalyticsProduct | FeedbackProduct | null,
): maybeIsPlan is Plan {
  return !!(maybeIsPlan && 'identifier' in maybeIsPlan);
}

/**
 * Determines if there's any circumstance which makes a plan unavailable to upgrade to.
 */
export const usePlanAvailability = ({
  featureGroup,
  selectedPlan,
  recommendedPlan,
  sku,
}: UsePlanAvailabilityProps): boolean => {
  const { site, featureGate, isUpgradeRequest } = useContext(PlanComparisonColumnsContext);
  const hasFeatureGate = !!featureGate;
  const currentPlanId = site?.effectivePlanId;

  const siteAccount = site?.account;
  const accountDto = siteAccount ? mapSiteAccountModelToDto(siteAccount) : undefined;
  const { planLt } = usePricingTableByAccount(accountDto);
  const { productLt } = useProductComparators();

  let isCurrentPlan = false;

  // Site's plan ID can't be determined. Maybe the data didn't fully load yet? Edge-case.
  if (!currentPlanId) {
    return false;
  }

  if (isProduct(selectedPlan)) {
    isCurrentPlan = currentPlanId.includes(selectedPlan.sku);
  } else if (isPlan(selectedPlan)) {
    isCurrentPlan = currentPlanId === selectedPlan.identifier;
  }

  // The user is already on this plan
  if (isCurrentPlan) {
    return false;
  }

  // We don't allow downgrading through the plan comparison modal.
  if (sku && isProduct(selectedPlan)) {
    const [currentAnalyticsProd, currentFeedbackProd] = getProductSkusByPlanId(currentPlanId);
    if (sku === ProductType.Analytics && productLt(selectedPlan.sku, currentAnalyticsProd)) {
      return false;
    }
    if (productLt(selectedPlan.sku, currentFeedbackProd)) {
      return false;
    }
  } else if (isPlan(selectedPlan) && planLt(selectedPlan.identifier, currentPlanId)) {
    return false;
  }

  // If this is an upgrade request, but the column's feature group is lower than the requested plan's.
  if (
    isPlan(recommendedPlan) &&
    isUpgradeRequest &&
    recommendedPlan &&
    FeatureGroupEnum[featureGroup] < FeatureGroupEnum[recommendedPlan.featureGroup]
  ) {
    return false;
  }
  if (
    isProduct(recommendedPlan) &&
    isUpgradeRequest &&
    recommendedPlan &&
    FeatureGroupEnum[featureGroup] < FeatureGroupEnum[recommendedPlan.tier]
  ) {
    return false;
  }

  // If there's a feature gate, and we have a static recommendation
  // we allow plan selection (as long as its not a downgrade)
  // else we allow only its minimum feature group or higher to be bought.
  if (
    hasFeatureGate &&
    !featureGate.staticRecommendedFeatureGroup &&
    featureGate?.minimumFeatureGroup
  ) {
    return FeatureGroupEnum[featureGroup] >= FeatureGroupEnum[featureGate.minimumFeatureGroup];
  }

  // We couldn't find any reason to deny upgrading to this feature group, so let's allow it.
  return true;
};
